import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// The graph-driven player.
//
// This is the web half of the Phase 2 runtime repoint. It runs the SAME
// `advance()` from @little-loop/content-engine that the v1 runtime endpoint and
// the Swift mirror on iOS run — never a second interpretation of routing. The
// legacy `TutorialPlayer` (v2 `branches` / `next_card_id`) stays beside it only
// until the Phase 7 sweep removes it.
//
// It renders from a PUBLISHED SNAPSHOT (`resolve_workflow_for_recipient`), so
// there is no template fallback left to mask a half-rebuilt workflow: a broken
// or unpublished workflow reads as blocked/empty rather than quietly serving
// stale content. Loud by design.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { advance, buildAnswer, buildFacts, isInputBlock, startSession, } from '@little-loop/content-engine';
import { InputBlock } from './blocks/InputBlock';
import { BlockView, EmptyState, OutcomeScreen, PlayerErrorState, visibleBlocks, } from './CardBody';
export function WorkflowPlayer({ graph, cards, workflowVersionId, sessionVariables = [], facts = {}, variableValues = {}, variableExamples = {}, outcomes = {}, mode = 'play', onComplete, onCardChange, onProgress, onError, header, }) {
    const declared = sessionVariables;
    // Enter the start node once. `advance` on a fresh state consumes no answer,
    // it just enters the graph.
    const [result, setResult] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const startedAtRef = useRef(null);
    if (startedAtRef.current === null)
        startedAtRef.current = Date.now();
    useEffect(() => {
        const initial = startSession(workflowVersionId, declared);
        setResult(advance(graph, initial, undefined, { externalFacts: facts, declared }));
        // Re-entering on a new snapshot is correct; re-entering because the caller
        // passed a fresh object literal is not, so the identity of the graph and the
        // version id are what gate this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graph, workflowVersionId]);
    const state = result?.state ?? null;
    const cardNode = result?.status === 'card' ? result.card : null;
    const snapshotCard = cardNode ? cards[cardNode.card_id] : undefined;
    /**
     * The live fact map: scoped external facts plus everything the session has
     * accumulated. This is what routing saw, so block visibility must evaluate
     * against the same thing — otherwise a card can route one way and render as
     * though it went another.
     */
    const liveFacts = useMemo(() => (state ? buildFacts(state, facts) : facts), [state, facts]);
    /**
     * Interpolation values: canonical scoped values from the resolver, plus live
     * session variables under `session.*` so a recap card can read
     * `{{session.score}}`.
     */
    const vars = useMemo(() => {
        const values = { ...variableValues };
        for (const [name, value] of Object.entries(state?.variables ?? {})) {
            values[`session.${name}`] = String(value);
        }
        return { values, examples: variableExamples, mode };
    }, [variableValues, variableExamples, mode, state?.variables]);
    const blocks = useMemo(() => snapshotCard?.blocks ?? [], [snapshotCard]);
    const shown = useMemo(() => visibleBlocks(blocks, liveFacts), [blocks, liveFacts]);
    const inputBlock = useMemo(() => blocks.find(isInputBlock), [blocks]);
    /** How many times the current node has been entered — 2+ means a retry loop. */
    const currentVisitCount = cardNode && state ? (state.visits[cardNode.id] ?? 1) : 0;
    // Notify on card entry, and surface the re-read count Phase 6 persists.
    const lastNotifiedRef = useRef(null);
    useEffect(() => {
        if (!cardNode || !state)
            return;
        const key = `${cardNode.id}:${currentVisitCount}`;
        if (lastNotifiedRef.current === key)
            return;
        lastNotifiedRef.current = key;
        onCardChange?.(cardNode.card_id, cardNode.id);
        onProgress?.({
            cardId: cardNode.card_id,
            nodeId: cardNode.id,
            viewCount: currentVisitCount,
        });
    }, [cardNode, state, currentVisitCount, onCardChange, onProgress]);
    // Clear the pending answer whenever a new card is entered, including a
    // re-entry of the same card on a retry loop.
    useEffect(() => {
        setInputValue('');
    }, [cardNode?.id, currentVisitCount]);
    useEffect(() => {
        if (result?.status === 'error' && result.error) {
            onError?.(new Error(result.error));
        }
    }, [result?.status, result?.error, onError]);
    useEffect(() => {
        if (result?.status !== 'outcome' || !state || mode === 'preview')
            return;
        onComplete?.({
            outcomeCode: state.outcome_code,
            path: Object.keys(state.visits),
            answers: state.answers,
            correct: state.correct,
            sessionValues: state.variables,
            visits: state.visits,
            restarts: state.restarts,
            workflowVersionId,
            completedAt: new Date().toISOString(),
            durationSeconds: Math.max(0, Math.round((Date.now() - (startedAtRef.current ?? Date.now())) / 1000)),
        });
        // onComplete fires once per outcome, not on every render that follows it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.status, state?.outcome_code]);
    const onContinue = useCallback(() => {
        if (!result || !state)
            return;
        const answer = buildAnswer(blocks, inputValue) ?? undefined;
        setResult(advance(graph, state, answer, { externalFacts: facts, declared }));
    }, [result, state, blocks, inputValue, graph, facts, declared]);
    if (!result) {
        return _jsx(EmptyState, { message: "Loading\u2026" });
    }
    if (result.status === 'error') {
        // Never a silent end-of-tutorial. This is the failure the graph model
        // exists to make visible.
        return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col", children: [header, _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto py-6", children: _jsx(PlayerErrorState, { message: result.error ?? 'Unknown routing error.' }) })] }));
    }
    if (result.status === 'outcome') {
        return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col", children: [header, _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto py-6", children: _jsx(OutcomeScreen, { code: state?.outcome_code ?? null, outcomes: outcomes }) })] }));
    }
    if (!snapshotCard) {
        // The graph references a card the snapshot doesn't carry. Publish
        // validation should make this unreachable; say so rather than render blank.
        return (_jsx(PlayerErrorState, { message: `This tutorial references card "${cardNode?.card_id}", which isn't in the published version.` }));
    }
    // A card that resolves to nothing. Two distinct causes, both of which used to
    // present as an indistinguishable blank screen:
    //
    //  - `blocks.length === 0` — an empty card. The publish gate rejects these
    //    now (`empty_card`, 20260727000800), but workflows published before that
    //    gate still carry them (ENG-228).
    //  - every block hidden by `visible_when` for THIS recipient's facts. The
    //    card is not empty, so no publish-time gate can catch it — it only exists
    //    per-recipient at runtime (ENG-177's coverage matrix is where this gets
    //    caught at author time).
    //
    // Either way the learner gets an explicit message rather than a void.
    const nothingToShow = shown.length === 0 && !inputBlock;
    if (nothingToShow) {
        console.warn(blocks.length === 0
            ? `[tutorial-player] card ${cardNode?.card_id} has no blocks at all — empty published content`
            : `[tutorial-player] every block on card ${cardNode?.card_id} is hidden for these facts — the recipient sees an empty card`);
    }
    const canAdvance = !inputBlock || !inputBlock.input_required || inputValue.trim().length > 0;
    return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col", children: [header, _jsx("div", { className: header ? 'pt-3' : 'pt-6', children: _jsx(GraphProgressBar, { graph: graph, state: state }) }), _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto py-6", children: _jsx(AnimatePresence, { mode: "wait", children: _jsxs(motion.section
                    // Keyed on the node alone. A composite key that also carried the
                    // visit count re-animated a re-entered card, but two key changes in
                    // quick succession interrupt the exit animation and `mode="wait"`
                    // then holds the OUTGOING card forever — the session advances while
                    // the screen stays frozen on the previous card. A missing
                    // re-animation on a retry loop is a far cheaper defect.
                    , { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.22, ease: 'easeOut' }, className: "flex flex-col gap-5", "aria-live": "polite", children: [nothingToShow ? (_jsx(EmptyState, { message: "There's nothing to show here for this profile." })) : (shown.map((b) => _jsx(BlockView, { block: b, vars: vars }, b.id))), inputBlock ? (_jsx(InputBlock, { block: inputBlock, value: inputValue, onChange: setInputValue, vars: vars })) : null] }, cardNode?.id ?? 'none') }) }), _jsx("div", { className: "border-t border-slate-100 pb-[max(env(safe-area-inset-bottom),12px)] pt-4", children: _jsx("button", { type: "button", onClick: onContinue, disabled: !canAdvance || mode === 'preview', className: "flex h-12 w-full items-center justify-center rounded-full bg-teal-500 text-base font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:bg-teal-600", children: inputBlock ? 'Check' : 'Continue' }) })] }));
}
/**
 * Progress across the graph's CARD nodes.
 *
 * Conditionals and forks are routing, not steps, so counting them would make
 * the bar jump. Loops mean visited can exceed the node count, hence the clamp —
 * a branching workflow has no single "length", so this is deliberately an
 * approximation rather than a promise.
 */
function GraphProgressBar({ graph, state, }) {
    const totalCards = graph.nodes.filter((n) => n.type === 'card').length;
    const cardNodeIds = new Set(graph.nodes.filter((n) => n.type === 'card').map((n) => n.id));
    const visited = Object.keys(state?.visits ?? {}).filter((id) => cardNodeIds.has(id)).length;
    const fraction = totalCards === 0 ? 0 : Math.min(visited / totalCards, 1);
    return (_jsx("div", { className: "h-1.5 w-full overflow-hidden rounded-full bg-slate-100", role: "progressbar", "aria-valuenow": Math.round(fraction * 100), "aria-valuemin": 0, "aria-valuemax": 100, children: _jsx(motion.div, { className: "h-full rounded-full bg-teal-500", initial: false, animate: { width: `${fraction * 100}%` }, transition: { duration: 0.3, ease: 'easeOut' } }) }));
}
